import React from "react";
import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import CalendarIcon from "../../assets/icons/calendar.svg2";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import ScheduleIcon from "../../assets/icons/scheduling.svg2";
import {
  ScheduleType,
  useCreateOrUpdateScheduleMutation,
  useGetSchedulerByUuidQuery,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { InputGroup, Tab, Tabs } from "react-bootstrap";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import DatePicker from "react-datepicker";
import SchedulersList from "./SchedulersList";
import { pageProps } from "../helpers/pageProps";
import timezones from "../Builder/nodes/Logic/timezones";

export default function ScheduleEdit({
  uuid,
  broadcasterUuid,
  projectUuid,
  triggerData,
  onSave,
  onClose,
}: {
  uuid?: string;
  broadcasterUuid?: string;
  projectUuid?: string;
  triggerData?: any;
  onSave?: () => void;
  onClose?: () => void;
}) {
  // run once: MaxRuns = 1, StartDate = date in future, EndDate = undefined
  // run forever: MaxRuns = 0, StartDate = date in future, EndDate = undefined
  // run x times: MaxRuns = x, StartDate = date in future, EndDate = undefined
  // run until: MaxRuns = 0, StartDate = date in future, EndDate = date in future
  const newScheduleData: ScheduleType = {
    Uuid: uuid,
    Label: "",
    Description: "",
    ProjectUuid: projectUuid,
    BroadcasterUuid: broadcasterUuid,
    BroadcastSendList: "",
    BroadcastContactUuids: [],
    BroadcastGroups: [],
    TriggerData: triggerData || {},
    IsActive: true,
    StartDate: new Date().toISOString().slice(0, 10),
    EndDate: undefined,
    MaxRuns: 1,
    FrequencyKind: "interval",
    IntervalValue: 1,
    IntervalUnit: "day",
    IntervalRunAtHour: 0,
    IntervalRunAtMinute: 0,
    WeekDays: [1, 2, 3, 4, 5, 6, 7],
    ExceptionDates: [],
    Hours: [],
    Minutes: [],
    TimeZone: pageProps.defaultTZ || "Europe/London",
    IsAdvanced: false,
  };

  const [scheduleData, setScheduleData] =
    useState<ScheduleType>(newScheduleData);
  console.log({ triggerData, newScheduleData, scheduleData });
  const [previousRepeatType, setPreviousRepeatType] = useState<
    "ONCE" | "X_TIMES" | undefined
  >();

  const [save] = useCreateOrUpdateScheduleMutation();

  const { data: existingSchedule } = useGetSchedulerByUuidQuery(
    { uuid: uuid! },
    { skip: !uuid }
  );

  useEffect(() => {
    if (existingSchedule) {
      setScheduleData(existingSchedule);
    }
  }, [existingSchedule]);

  useEffect(() => {
    if (triggerData) {
      setScheduleData((prev) => ({
        ...prev,
        TriggerData: triggerData,
      }));
    }
  }, [triggerData]);

  const repeatSelectValue =
    scheduleData.MaxRuns === 0 && scheduleData.EndDate
      ? "until"
      : scheduleData.MaxRuns === 0
      ? "0"
      : scheduleData.MaxRuns === 1
      ? previousRepeatType === "X_TIMES"
        ? "2"
        : "1"
      : "2";
  const showRunXTimesInput = repeatSelectValue === "2";

  const [xTimesError, setXTimesError] = useState("");

  return (
    <>
      <div className="p-4 pt-0">
        <div className="d-flex justify-content-end">
          <Form.Check
            id="schedule-is-active-switch"
            type={"switch"}
            label="Active"
            checked={scheduleData.IsActive}
            onChange={(e) =>
              setScheduleData((prev) => ({
                ...prev,
                IsActive: e.target.checked,
              }))
            }
          />
        </div>
        <div className="fw-bold mt-2">Label</div>
        <Form.Control
          type="text"
          placeholder="Schedule label"
          className="mb-3 bg-white rounded-2 input-wrapper"
          value={scheduleData.Label}
          onChange={(e) =>
            setScheduleData((prev) => ({
              ...prev,
              Label: e.target.value,
            }))
          }
        />
        <div className="fw-bold mt-2">Description</div>

        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Schedule description"
          className="mb-3 bg-white rounded-2 input-wrapper"
          value={scheduleData.Description}
          onChange={(e) =>
            setScheduleData((prev) => ({
              ...prev,
              Description: e.target.value,
            }))
          }
        />

        <div className="fw-bold mt-2">Start date</div>
        <div className="d-flex">
          <div className="d-flex align-items-center bg-white rounded-2 w-50 input-wrapper ps-2">
            <CalendarIcon width={30} className="bg-icon p-1 rounded-2" />{" "}
            <div className="flex-grow-1">
              <Input
                inputType="dateTime"
                dateTimeType="date"
                value={[scheduleData.StartDate?.slice(0, 10)]}
                onChange={([v]) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    StartDate: v,
                  }));
                }}
              />
            </div>
          </div>
          {!scheduleData.IsAdvanced && (
            <div className="bg-white rounded-2 flex-grow-1 ms-2">
              <div className="input-group">
                <Form.Select
                  aria-label="Default select example"
                  value={scheduleData.IntervalRunAtHour}
                  onChange={(e) => {
                    setScheduleData((prev) => ({
                      ...prev,
                      IntervalRunAtHour: parseInt(e.target.value) || 0,
                    }));
                  }}
                >
                  {new Array(24).fill(0).map((_, i) => (
                    <option key={i} value={i}>
                      {i} Hours
                    </option>
                  ))}
                </Form.Select>
                <InputGroup.Text className="fw-bold bg-white">
                  :
                </InputGroup.Text>
                <Form.Select
                  aria-label="Default select example"
                  value={scheduleData.IntervalRunAtMinute}
                  onChange={(e) => {
                    setScheduleData((prev) => ({
                      ...prev,
                      IntervalRunAtMinute: parseInt(e.target.value) || 0,
                    }));
                  }}
                >
                  {new Array(60).fill(0).map((_, i) => (
                    <option key={i} value={i}>
                      {i} Minutes
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          )}
        </div>

        <hr />

        <div className="fw-bold mb-3">
          Repeat this broadcast
          <div className="d-flex">
            <Form.Select // prettier-ignore
              id="broadcast-switch"
              className=""
              value={repeatSelectValue}
              onChange={(e) => {
                if (e.target.value === "until") {
                  setPreviousRepeatType(undefined);
                  setScheduleData((prev) => ({
                    ...prev,
                    MaxRuns: 0,
                    EndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .slice(0, 10),
                  }));
                  return;
                }
                if (e.target.value === "1") {
                  setPreviousRepeatType("ONCE");
                  setScheduleData((prev) => ({
                    ...prev,
                    MaxRuns: 1,
                    EndDate: undefined,
                    FrequencyKind: "interval",
                    IntervalValue: 1,
                    IntervalUnit: "day",
                    Hours: [],
                    Minutes: [],
                    WeekDays: [1, 2, 3, 4, 5, 6, 7],
                  }));
                  return;
                }
                if (e.target.value === "2") {
                  setPreviousRepeatType("X_TIMES");
                  setScheduleData((prev) => ({
                    ...prev,
                    MaxRuns: 2,
                    EndDate: undefined,
                  }));
                  return;
                }
                setPreviousRepeatType(undefined);
                setScheduleData((prev) => ({
                  ...prev,
                  MaxRuns: parseInt(e.target.value) || 0,
                  EndDate: undefined,
                }));
              }}
            >
              <option value="0">Run forever</option>
              {!scheduleData.IsAdvanced && <option value="1">Run once</option>}
              <option value="2">Run x number of times</option>
              <option value="until">Run until</option>
            </Form.Select>
            {showRunXTimesInput && (
              <InputGroup className="ms-2">
                <Form.Control
                  type="number"
                  min={2}
                  placeholder="Number of runs"
                  defaultValue={scheduleData.MaxRuns}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (isNaN(value)) {
                      setXTimesError("Please enter a valid number.");
                      return;
                    }
                    if (value < 2) {
                      setXTimesError(
                        "Number of runs must be at least 2. If you want to run once, please select 'Run once' option."
                      );
                      return;
                    }
                    setXTimesError("");
                    setScheduleData((prev) => ({
                      ...prev,
                      MaxRuns: Math.max(2, value),
                    }));
                  }}
                  onBlur={(e) => {
                    e.target.value = scheduleData.MaxRuns.toString();
                    setXTimesError("");
                  }}
                />
                <InputGroup.Text className="bg-white">Times</InputGroup.Text>
              </InputGroup>
            )}
            {scheduleData.MaxRuns === 0 && !!scheduleData.EndDate && (
              <div className="d-flex align-items-center bg-white rounded-2 input-wrapper ps-2 ms-2">
                <CalendarIcon width={30} className="bg-icon p-1 rounded-2" />{" "}
                <div className="flex-grow-1">
                  <Input
                    inputType="dateTime"
                    dateTimeType="date"
                    value={[scheduleData.EndDate?.slice(0, 10)]}
                    onChange={([v]) => {
                      setScheduleData((prev) => ({
                        ...prev,
                        EndDate: v,
                      }));
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {xTimesError && (
            <div className="text-danger d-flex justify-content-end">
              {xTimesError}
            </div>
          )}
        </div>

        {scheduleData.IsAdvanced && (
          <>
            <div className="mb-3">
              <Input
                inputType={"button"}
                label=""
                options={[
                  {
                    label: "Repeat in intervals",
                    value: "interval",
                  },
                  { label: "Repeat daily", value: "fixed" },
                ]}
                value={[scheduleData.FrequencyKind]}
                onChange={([value]) => {
                  if (!value) return;
                  setScheduleData((prev) => ({
                    ...prev,
                    FrequencyKind: value,
                    Hours: [],
                    Minutes: [],
                  }));
                }}
              />
            </div>
          </>
        )}

        {scheduleData.FrequencyKind === "interval" &&
          scheduleData.MaxRuns !== 1 && (
            <div className="input-group mb-3">
              <InputGroup.Text className="bg-white">Every</InputGroup.Text>
              <Form.Control
                type="number"
                aria-label="Default select example"
                value={scheduleData.IntervalValue}
                onChange={(e) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    IntervalValue: parseInt(e.target.value) || 1,
                  }));
                }}
              />

              <Form.Select
                aria-label="Default select example"
                value={scheduleData.IntervalUnit}
                onChange={(e) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    IntervalUnit: e.target.value as "minute" | "hour" | "day",
                  }));
                }}
                disabled={!scheduleData.IsAdvanced}
              >
                <option value="minute">Minutes</option>
                <option value="hour">Hours</option>
                <option value="day">Days</option>
              </Form.Select>
            </div>
          )}

        {scheduleData.IsAdvanced &&
          scheduleData.FrequencyKind === "interval" &&
          scheduleData.MaxRuns !== 1 &&
          ["day", "hour"].includes(scheduleData.IntervalUnit) && (
            <div className="input-group mb-3">
              <InputGroup.Text className="bg-white">At</InputGroup.Text>
              {["day"].includes(scheduleData.IntervalUnit) && (
                <Form.Select
                  aria-label="Default select example"
                  value={scheduleData.IntervalRunAtHour}
                  onChange={(e) => {
                    setScheduleData((prev) => ({
                      ...prev,
                      IntervalRunAtHour: parseInt(e.target.value) || 1,
                    }));
                  }}
                >
                  {new Array(24).fill(0).map((_, i) => (
                    <option key={i} value={i}>
                      {i} Hours
                    </option>
                  ))}
                </Form.Select>
              )}

              <Form.Select
                aria-label="Default select example"
                value={scheduleData.IntervalRunAtMinute}
                onChange={(e) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    IntervalRunAtMinute: parseInt(e.target.value) || 0,
                  }));
                }}
              >
                {new Array(60).fill(0).map((_, i) => (
                  <option key={i} value={i}>
                    {i} Minutes
                  </option>
                ))}
              </Form.Select>
            </div>
          )}

        {scheduleData.FrequencyKind === "fixed" && (
          <div className="d-flex align-items-center mb-3">
            <div className="flex-grow-1">
              At hours
              <Input
                inputType="select"
                isMulti
                value={scheduleData.Hours}
                onChange={(v) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    Hours: v,
                  }));
                }}
                options={new Array(24).fill(0).map((_, i) => ({
                  label: `${i}`,
                  value: i,
                }))}
              />
            </div>
            <div className="flex-grow-1">
              At minutes
              <Input
                inputType="select"
                isMulti
                value={scheduleData.Minutes}
                onChange={(v) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    Minutes: v,
                  }));
                }}
                options={new Array(60).fill(0).map((_, i) => ({
                  label: `${i}`,
                  value: i,
                }))}
              />
            </div>
          </div>
        )}
        <hr />

        {scheduleData.MaxRuns !== 1 && (
          <div>
            <div className="mt-2 fw-bold">Run on weekdays</div>
            <div className="text-secondary">
              Your schedule will run on the selected weekdays only.
            </div>
            <Input
              inputType="select"
              isMulti
              value={scheduleData.WeekDays}
              onChange={(values) => {
                setScheduleData((prev) => ({
                  ...prev,
                  WeekDays: values.includes("all")
                    ? [1, 2, 3, 4, 5, 6, 7]
                    : values,
                }));
              }}
              options={[
                { label: "All week", value: "all" },
                { label: "Monday", value: 1 },
                { label: "Tuesday", value: 2 },
                { label: "Wednesday", value: 3 },
                { label: "Thursday", value: 4 },
                { label: "Friday", value: 5 },
                { label: "Saturday", value: 6 },
                { label: "Sunday", value: 7 },
              ]}
            />
          </div>
        )}

        {scheduleData.MaxRuns !== 1 && (
          <div className="mt-3">
            <div className="d-flex justify-content-between w-100">
              <div className="fw-bold">Exception dates</div>
              <div>
                <DatePicker
                  customInput={
                    <span className="btn btn-link p-0">Add exception date</span>
                  }
                  selected={null}
                  onChange={(value: Date | null) => {
                    const dateStr =
                      value instanceof Date
                        ? value.getFullYear().toString().padStart(4, "0") +
                          "-" +
                          (value.getMonth() + 1).toString().padStart(2, "0") +
                          "-" +
                          value.getDate().toString().padStart(2, "0")
                        : value || "";
                    if (
                      dateStr &&
                      scheduleData.ExceptionDates.includes(dateStr)
                    )
                      return;

                    const newExceptionDates = [
                      ...scheduleData.ExceptionDates,
                      dateStr,
                    ];

                    setScheduleData((prev) => ({
                      ...prev,
                      ExceptionDates: newExceptionDates,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="text-secondary">
              Your schedule will not run on the selected dates.
            </div>

            {scheduleData.ExceptionDates.length > 0 && (
              <div className="d-flex align-items-center flex-wrap bg-white rounded-2 p-2">
                {scheduleData.ExceptionDates.map((d, i) => (
                  <span
                    className="badge bg-colored rounded-pill ps-2 p-0 me-1"
                    key={i}
                  >
                    {d}
                    <button
                      className="btn btn-outline-light border-0 btn-sm ms-2 p-0 px-1 rounded-pill"
                      onClick={() => {
                        setScheduleData((prev) => ({
                          ...prev,
                          ExceptionDates: prev.ExceptionDates.filter(
                            (date) => date !== d
                          ),
                        }));
                      }}
                    >
                      <FontAwesomeIcon icon={faX} className="" size="2xs" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-3">
          <div className="">
            <div className="fw-bold">Time zone</div>
            <div>
              <Input
                inputType="select"
                value={[scheduleData.TimeZone]}
                onChange={([v]) => {
                  setScheduleData((prev) => ({
                    ...prev,
                    TimeZone: v,
                  }));
                }}
                options={timezones.map((tz) => ({
                  label: tz.label,
                  value: tz.tzCode,
                }))}
              />
            </div>
          </div>
        </div>

        <div className="my-3">
          <Form.Check // prettier-ignore
            type={"switch"}
            id={`show-advanced-settings}`}
            label={`Show Advanced Controls`}
            checked={scheduleData.IsAdvanced}
            onChange={(e) => {
              setScheduleData((prev) => ({
                ...prev,
                IsAdvanced: e.target.checked,
              }));
              if (e.target.checked) {
                if (scheduleData.MaxRuns === 1) {
                  setPreviousRepeatType("ONCE");
                  setScheduleData((prev) => ({
                    ...prev,
                    MaxRuns: 0,
                  }));
                }
              } else {
                if (previousRepeatType === "ONCE") {
                  setScheduleData((prev) => ({
                    ...prev,
                    MaxRuns: 1,
                    EndDate: undefined,
                    FrequencyKind: "interval",
                    IntervalValue: 1,
                    IntervalUnit: "day",
                    Hours: [],
                    Minutes: [],
                    WeekDays: [1, 2, 3, 4, 5, 6, 7],
                  }));
                  setPreviousRepeatType(undefined);
                }
              }
            }}
          />
        </div>
      </div>

      <JsonCollapse json={scheduleData} label="Schedule data" />
      <div className="p-2 d-flex justify-content-end">
        {onClose && (
          <button
            className="btn bg-black text-white rounded-pill  fw-bold  px-3 py-1  me-2 text-nowrap"
            onClick={onClose}
          >
            CANCEL
          </button>
        )}
        <button
          className="btn fw-bold  px-3 py-1  me-2  bg-colored rounded-pill text-white"
          disabled={!scheduleData.Label || !scheduleData.StartDate}
          onClick={async () => {
            const res = await save(scheduleData);
            if ("data" in res) {
              toast.success("Schedule saved successfully");
              onSave?.();
              setScheduleData(newScheduleData);
            } else {
              toast.error(
                "Failed to save schedule",
                res.error.data?.message || ""
              );
            }
          }}
        >
          SAVE{" "}
        </button>
      </div>
    </>
  );
}
